import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import '../../style/dashboard.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VoterCaptainWiseSurvey = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            // Page titles
            title: "Voter Captain Wise Survey",
            subtitle: "Overview of survey progress and reports.",
            
            // Search
            searchPlaceholder: "Search voter captain...",
            
            // Messages
            loading: "Loading Data...",
            noRecordsFound: "No voter captain found matching",
            
            // Labels
            totalSurvey: "Total Survey",
            available: "Available",
            unavailable: "Unavailable",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            none: "None",
            
            // Call button title
            call: "Call"
        },
        
        hi: {
            // Page titles
            title: "वोटर कैप्टन वार सर्वेक्षण",
            subtitle: "सर्वेक्षण प्रगति और रिपोर्ट का अवलोकन।",
            
            // Search
            searchPlaceholder: "वोटर कैप्टन खोजें...",
            
            // Messages
            loading: "डेटा लोड हो रहा है...",
            noRecordsFound: "कोई वोटर कैप्टन नहीं मिला",
            
            // Labels
            totalSurvey: "कुल सर्वेक्षण",
            available: "उपलब्ध",
            unavailable: "अनुपलब्ध",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            none: "कोई नहीं",
            
            // Call button title
            call: "कॉल"
        },
        
        gu: {
            // Page titles
            title: "વોટર કેપ્ટન વાઈઝ સર્વે",
            subtitle: "સર્વે પ્રગતિ અને રિપોર્ટ્સની ઝાંખી.",
            
            // Search
            searchPlaceholder: "વોટર કેપ્ટન શોધો...",
            
            // Messages
            loading: "ડેટા લોડ થઈ રહ્યો છે...",
            noRecordsFound: "કોઈ વોટર કેપ્ટન મળ્યો નથી",
            
            // Labels
            totalSurvey: "કુલ સર્વે",
            available: "ઉપલબ્ધ",
            unavailable: "અનુપલબ્ધ",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            none: "કોઈ નહીં",
            
            // Call button title
            call: "કૉલ"
        }
    };

    const txt = t[lang];
    
    const [voterCaptains, setVoterCaptains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (appId) {
            fetchData();
        }
    }, [appId]);

    const fetchData = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_user_type_wise_survey_dash`, {
                app_id: appId,
                type: "VC"
            });

            if (response.data.success) {
                setVoterCaptains(response.data.data.result || []);
            }
        } catch (error) {
            console.error("Error fetching voter captain wise survey:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Navigation Handler ---
    const handleVoterCaptainClick = (userId) => {
        navigate('/dis_user_wise_survey_voter', { state: { user_id: userId, user_type: "VC" } });
    };  

    // Filter Logic
    const filteredVoterCaptains = voterCaptains.filter(voterCaptain => 
        (voterCaptain.name && voterCaptain.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (voterCaptain.mobile_no && voterCaptain.mobile_no.includes(searchTerm))
    );

    return (
        <div className="app-container dashboard-page">
            <div className='dashboard-content container-fluid p-4'>
                
                {/* --- Header Component Integrated Here --- */}
                <Header 
                    title={txt.title} 
                />

                {/* --- Search Bar Row --- */}
                <div className="row fade-in-up mt-4" style={{ animationDelay: '0.1s' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3">
                            <div className="search-box-wrapper">
                                <svg className="search-icon" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input 
                                    type="text" 
                                    className="header-search-input" 
                                    placeholder={txt.searchPlaceholder}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Content List --- */}
                <div className="container-fluid mt-4" style={{ flex: 1 }}>
                    {loading ? (
                        <div className="text-center p-5 text-muted">{txt.loading}</div>
                    ) : (
                        <div className="row g-4">
                            {filteredVoterCaptains.length > 0 ? (
                                filteredVoterCaptains.map((voterCaptain) => {
                                    const completed = (voterCaptain.P || 0) + (voterCaptain.N || 0) + (voterCaptain.D || 0) + (voterCaptain.C || 0);

                                    return (
                                        <div key={voterCaptain.user_id} className="col-12 col-md-6 col-lg-4 fade-in-up">
                                            <div className="user-survey-card h-100" style={{ cursor: 'pointer' }} onClick={() => handleVoterCaptainClick(voterCaptain.user_id)}>
                                                <div className="card-top">
                                                    <div className="user-avatar-wrapper">
                                                        {voterCaptain.photo ? (
                                                            <img src={voterCaptain.photo_path} alt={voterCaptain.name} />
                                                        ) : (
                                                            <div className="avatar-placeholder">{voterCaptain.name?.charAt(0)}</div>
                                                        )}
                                                    </div>
                                                    <div className="user-info">
                                                        <h3 className="user-name">{voterCaptain.name}</h3>
                                                        
                                                        <div className="user-details-row">
                                                            <span className="user-mobile">{voterCaptain.mobile_no}</span>
                                                            <span className="dot-separator">•</span>
                                                            <span className="user-designation">{voterCaptain.designation || voterCaptain.user_type}</span>
                                                        </div>
                                                    </div>

                                                    <div className="card-top-right">
                                                        <span className="total-survey-text">{txt.totalSurvey} : {voterCaptain.total_survey}</span>
                                                        {/* Stop propagation on the call button to prevent navigating the card */}
                                                        <a 
                                                            href={`tel:${voterCaptain.mobile_no}`} 
                                                            className="call-btn-mini" 
                                                            title={txt.call}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <svg width="14" height="14" fill="#3b82f6" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                                                        </a>
                                                    </div>
                                                </div>

                                                <div className="card-middle-grid">
                                                    <div className="avail-box">
                                                        <span className="avail-text">{txt.available} : {completed}</span>
                                                    </div>
                                                    <div className="avail-box">
                                                        <span className="avail-text">{txt.unavailable} : {voterCaptain.NA || 0}</span>
                                                    </div>
                                                </div>

                                                <div className="card-stats-grid">
                                                    <div className="stat-box stat-green">
                                                        <span className="stat-lbl">{txt.positive}</span>
                                                        <span className="stat-val text-green">{voterCaptain.P || 0}</span>
                                                    </div>
                                                    <div className="stat-box stat-red">
                                                        <span className="stat-lbl">{txt.negative}</span>
                                                        <span className="stat-val text-red">{voterCaptain.N || 0}</span>
                                                    </div>
                                                    <div className="stat-box stat-orange">
                                                        <span className="stat-lbl">{txt.doubtful}</span>
                                                        <span className="stat-val text-orange">{voterCaptain.D || 0}</span>
                                                    </div>
                                                    <div className="stat-box stat-blue">
                                                        <span className="stat-lbl">{txt.none}</span>
                                                        <span className="stat-val text-blue">{voterCaptain.C || 0}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="col-12 text-center text-muted p-4">
                                    {txt.noRecordsFound} "{searchTerm}"
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .search-box-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 300px;
                }
                .header-search-input {
                    width: 100%;
                    padding: 10px 10px 10px 40px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    outline: none;
                    font-size: 14px;
                    background-color: white;
                    color: #1e293b;
                    transition: border-color 0.2s;
                }
                .header-search-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                }
                .user-survey-card {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 2px solid black;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .user-survey-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
                }
                .card-top {
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    border-bottom: 1px solid #f8fafc;
                    position: relative;
                }
                .user-avatar-wrapper {
                    flex-shrink: 0;
                }
                .user-avatar-wrapper img {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 2px solid #fff;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                .avatar-placeholder {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background-color: #eef0f2;
                    color: #94a3b8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 22px;
                    text-transform: uppercase;
                }
                .user-info {
                    flex: 1;
                    min-width: 0;
                }
                .card-top-right {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 8px;
                    flex-shrink: 0;
                }
                .total-survey-text {
                    font-size: 13px;
                    font-weight: 700;
                    color: #1e293b;
                    white-space: nowrap;
                }
                .user-name {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 6px 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .user-details-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    width: 100%;
                }
                .user-mobile {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0;
                    font-weight: 500;
                }
                .dot-separator {
                    font-size: 10px;
                    color: #cbd5e1;
                }
                .user-designation {
                    font-size: 12px;
                    font-weight: 600;
                    color: #475569;
                    background-color: #f1f5f9;
                    padding: 2px 8px;
                    border-radius: 4px;
                    white-space: nowrap;
                    max-width: 120px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .call-btn-mini {
                    width: 30px;
                    height: 30px;
                    background-color: #eaf2fe;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: 0.2s;
                }
                .call-btn-mini:hover {
                    background-color: #d6e6fd;
                    transform: scale(1.1);
                }
                .card-middle-grid {
                    padding: 16px 20px 8px 20px;
                    display: flex;
                    gap: 10px;
                    background-color: #fff;
                }
                .avail-box {
                    flex: 1;
                    border-radius: 10px;
                    padding: 12px 8px;
                    text-align: center;
                    background-color: #f5f6f8;
                }
                .avail-text {
                    font-size: 13px;
                    font-weight: 700;
                    color: #334155;
                }
                .card-stats-grid {
                    display: flex;
                    gap: 10px;
                    padding: 8px 20px 20px 20px;
                    background-color: #fff;
                }
                .stat-box {
                    flex: 1;
                    border-radius: 10px;
                    padding: 10px 4px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .stat-green { background-color: #e8f8ee; }
                .stat-red { background-color: #fdecec; }
                .stat-orange { background-color: #fdf3e3; }
                .stat-blue { background-color: #e9f1fd; }
                .stat-val { font-size: 16px; font-weight: 800; }
                .stat-lbl { font-size: 11px; font-weight: 600; color: #64748b; }
                .text-green { color: #16a34a; }
                .text-red { color: #ef4444; }
                .text-orange { color: #f59e0b; }
                .text-blue { color: #3b82f6; }

                /* =========================================
                   RESPONSIVE MEDIA QUERIES (MOBILE FIXES)
                   ========================================= */
                @media (max-width: 992px) {
                    .col-lg-3 {
                        flex: 0 0 auto;
                        width: 50%;
                    }
                }

                @media (max-width: 768px) {
                    .search-box-wrapper {
                        max-width: 100%;
                    }
                    .page-header {
                        align-items: flex-start !important;
                    }
                    .header-content {
                        margin-bottom: 8px;
                    }
                    .card-top {
                        padding: 16px;
                    }
                    .card-middle-grid {
                        padding: 12px 16px 6px 16px;
                        gap: 8px;
                    }
                    .card-stats-grid {
                        padding: 6px 16px 16px 16px;
                        gap: 8px;
                    }
                    .avail-text {
                        font-size: 12px;
                    }
                    .col-md-6 {
                        flex: 0 0 auto;
                        width: 50%;
                    }
                    /* Remove padding around cards on mobile */
                    .dashboard-content {
                        padding: 0 !important;
                    }
                    .container-fluid {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    .row.g-4 {
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                    }
                    .row.g-4 > [class*="col-"] {
                        padding-left: 4px !important;
                        padding-right: 4px !important;
                    }
                    .user-survey-card {
                        border-radius: 8px;
                    }
                }

                @media (max-width: 480px) {
                    .col-12 {
                        flex: 0 0 auto;
                        width: 100%;
                    }
                    .user-avatar-wrapper img, .avatar-placeholder {
                        width: 46px;
                        height: 46px;
                        font-size: 16px;
                    }
                    .user-name {
                        font-size: 15px;
                    }
                    .user-mobile {
                        font-size: 12px;
                    }
                    .user-designation {
                        font-size: 10px;
                        max-width: 90px;
                    }
                    .total-survey-text {
                        font-size: 11px;
                    }
                    .card-middle-grid {
                        padding: 10px 12px 5px 12px;
                        gap: 6px;
                    }
                    .card-stats-grid {
                        padding: 5px 12px 12px 12px;
                        gap: 6px;
                    }
                    .avail-text {
                        font-size: 11px;
                    }
                    .stat-val {
                        font-size: 14px;
                    }
                    .stat-lbl {
                        font-size: 9px;
                        letter-spacing: -0.5px;
                    }
                    /* Add little padding around cards on mobile */
                    .dashboard-content {
                        padding: 6px !important;
                    }
                    .container-fluid {
                        padding-left: 6px !important;
                        padding-right: 6px !important;
                    }
                    .row.g-4 {
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                    }
                    .row.g-4 > [class*="col-"] {
                        padding-left: 5px !important;
                        padding-right: 5px !important;
                    }
                    .user-survey-card {
                        border-radius: 6px;
                    }
                    .card-top {
                        padding: 12px;
                        gap: 10px;
                    }
                    .card-middle-grid {
                        padding: 8px 10px 4px 10px;
                        gap: 4px;
                    }
                    .card-stats-grid {
                        padding: 4px 10px 10px 10px;
                        gap: 4px;
                    }
                    .avail-box {
                        padding: 8px 4px;
                    }
                    .stat-box {
                        padding: 6px 2px;
                    }
                }
            `}</style>
        </div>
    );
};

export default VoterCaptainWiseSurvey;